import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { routeTree } from './routeTree.gen';
import DefaultError from './components/DefaultError';
import * as Sentry from '@sentry/react';
import './styles.css';
import { isProduction } from './lib/utils/envUtils';

const queryClient = new QueryClient();

const router = createRouter({
    routeTree,
    context: {
        queryClient,
    },
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
    defaultErrorComponent: DefaultError,
});

if (isProduction()) {
    Sentry.init({
        dsn: 'https://81a3159766ba1a1a33919e1db392e1cb@o4508413192634368.ingest.us.sentry.io/4508413194207232',
        integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router)],
        // Setting a sample rate is required for sending performance data.
        // We recommend adjusting this value in production, or using tracesSampler
        // for finer control.
        tracesSampleRate: 1.0,
    });
}

declare module '@tanstack/react-router' {
    interface Register {
        router: typeof router;
    }
}

const rootElement = document.getElementById('app')!;

if (!rootElement.innerHTML) {
    const root = ReactDOM.createRoot(
        rootElement,
        isProduction()
            ? {
                  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
                      console.warn('Uncaught error', error, errorInfo.componentStack);
                  }),
                  onCaughtError: Sentry.reactErrorHandler(),
                  onRecoverableError: Sentry.reactErrorHandler(),
              }
            : undefined,
    );
    root.render(
        <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
        </QueryClientProvider>,
    );
}
